import { useEffect, useRef, useState } from 'react';
import { ApiRequestError, postPreReviewInvoice } from '@/api';

function base64ToBlobUrl(base64: string, mimeType: string): string {
  const trimmed = base64.trim();
  let binary: string;
  try {
    binary = atob(trimmed);
  } catch {
    return '';
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

function base64ToDataUrl(base64: string, mimeType: string): string {
  const trimmed = base64.trim();
  if (trimmed.startsWith('data:')) return trimmed;
  return `data:${mimeType};base64,${trimmed}`;
}

type InvoicePreviewModalProps = {
  open: boolean;
  onClose: () => void;
};

export function InvoicePreviewModal({ open, onClose }: InvoicePreviewModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceBase64, setInvoiceBase64] = useState('');
  const [invoiceType, setInvoiceType] = useState<'pdf' | 'image'>('pdf');
  const [invoiceNumber, setInvoiceNumber] = useState<string | undefined>();
  const [previewBlobUrl, setPreviewBlobUrl] = useState('');
  const [embedFailed, setEmbedFailed] = useState(false);
  const blobUrlRef = useRef('');

  const mimeType = invoiceType === 'pdf' ? 'application/pdf' : 'image/jpeg';

  useEffect(() => {
    if (!invoiceBase64) {
      setPreviewBlobUrl('');
      return;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = '';
    }
    const url = base64ToBlobUrl(invoiceBase64, mimeType);
    blobUrlRef.current = url;
    setPreviewBlobUrl(url);
    setEmbedFailed(false);
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = '';
      }
    };
  }, [invoiceBase64, mimeType]);

  useEffect(() => {
    if (!open) {
      setInvoiceBase64('');
      setInvoiceType('pdf');
      setInvoiceNumber(undefined);
      setError('');
      setEmbedFailed(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setInvoiceBase64('');
    setInvoiceType('pdf');
    setInvoiceNumber(undefined);
    setEmbedFailed(false);

    postPreReviewInvoice()
      .then((result) => {
        if (cancelled) return;
        setInvoiceBase64(result.invoiceBase64);
        setInvoiceType(result.invoiceType);
        setInvoiceNumber(result.invoiceNumber);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiRequestError ? e.message : 'Failed to preview invoice.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleDownload = () => {
    if (!previewBlobUrl) return;
    const link = document.createElement('a');
    link.href = previewBlobUrl;
    const date = new Date().toISOString().slice(0, 10);
    const ext = invoiceType === 'pdf' ? 'pdf' : 'jpg';
    link.download = `Invoice_Preview_${date}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewWindow = () => {
    if (!previewBlobUrl && !invoiceBase64) return;
    const url = previewBlobUrl || base64ToDataUrl(invoiceBase64, mimeType);
    const win = window.open(url, '_blank');
    if (!win) handleDownload();
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-white flex flex-col"
      style={{ maxWidth: 480, margin: '0 auto' }}
      role="dialog"
      aria-modal="true"
      aria-label="Invoice preview"
    >
      <div className="flex-shrink-0 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2.5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 cursor-pointer flex-shrink-0 hover:bg-gray-200 transition-colors"
            aria-label="Close invoice preview"
          >
            <i className="ri-arrow-left-s-line text-gray-700 text-xl" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-gray-900 font-bold text-base truncate">Invoice Preview</div>
            <p className="text-gray-400 text-[11px] truncate">
              {invoiceNumber
                ? `Invoice No. ${invoiceNumber}`
                : 'Sample invoice from your saved settings'}
            </p>
          </div>
          {!loading && previewBlobUrl ? (
            <button
              type="button"
              onClick={handleDownload}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 cursor-pointer flex-shrink-0 hover:bg-gray-200 transition-colors"
              aria-label="Download invoice"
            >
              <i className="ri-download-line text-gray-600" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {loading && (
          <div className="flex-1 flex flex-col items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
            <p className="text-gray-400 text-xs mt-2">Generating invoice preview…</p>
          </div>
        )}

        {error && (
          <div className="px-3 pt-3">
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-2.5 py-2">
              {error}
            </div>
          </div>
        )}

        {!loading && previewBlobUrl && invoiceType === 'pdf' && !embedFailed && (
          <div className="flex-1 min-h-0 px-3 pt-3">
            <div className="relative h-full rounded-lg border border-gray-200 bg-gray-100 overflow-hidden">
              <object
                data={previewBlobUrl}
                type="application/pdf"
                width="100%"
                height="100%"
                className="absolute inset-0 w-full h-full bg-white"
              >
                <iframe
                  src={previewBlobUrl}
                  title="Invoice preview"
                  className="absolute inset-0 w-full h-full bg-white border-0"
                  onError={() => setEmbedFailed(true)}
                />
              </object>
            </div>
          </div>
        )}

        {!loading && previewBlobUrl && invoiceType !== 'pdf' && (
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-minimal px-3 pt-3">
            <div className="rounded-lg border border-gray-200 bg-gray-100 overflow-hidden">
              <img src={previewBlobUrl} alt="Invoice preview" className="w-full h-auto block" />
            </div>
          </div>
        )}

        {!loading && previewBlobUrl && invoiceType === 'pdf' && embedFailed && (
          <div className="flex-1 min-h-0 px-3 pt-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center space-y-3">
              <i className="ri-file-pdf-2-line text-4xl text-amber-500" />
              <p className="text-sm text-amber-800">PDF preview is not available in this browser.</p>
              <button
                type="button"
                onClick={handleOpenInNewWindow}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500 text-white text-xs font-normal cursor-pointer hover:bg-amber-600"
              >
                <i className="ri-external-link-line" />
                Open PDF in New Window
              </button>
            </div>
          </div>
        )}

        {!loading && previewBlobUrl && (
          <div className="flex-shrink-0 flex items-center justify-center gap-4 py-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <button
              type="button"
              onClick={handleOpenInNewWindow}
              className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 cursor-pointer py-1"
            >
              <i className="ri-external-link-line" />
              Open in new window
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 cursor-pointer py-1"
            >
              <i className="ri-download-line" />
              Download
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
